import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { GalleryMediaItem } from '../../services/api';

interface GalleryGridProps {
  media?: GalleryMediaItem[];
  onPhotoClick?: (index: number) => void;
  emptyLabel?: string;
  dense?: boolean;
  variant?: 'default' | 'compact';
}

const GalleryGrid: React.FC<GalleryGridProps> = ({
  media = [],
  onPhotoClick,
  emptyLabel = 'No photos available yet.',
  dense = false,
  variant = 'default',
}) => {
  const theme = useTheme();
  const aspectPadding = variant === 'compact' ? '70%' : '100%';
  const borderRadius = variant === 'compact' ? 1.5 : 2;
  const cardShadow = variant === 'compact' ? 1 : 2;

  if (!media || media.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          textAlign: 'center',
          color: 'text.secondary',
          backgroundColor: theme.palette.action.hover,
        }}
      >
        <Typography variant="body2">{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${dense ? 3 : 2}, 1fr)`,
          sm: `repeat(${dense ? 4 : 3}, 1fr)`,
        },
        gap: dense ? 1 : 1.5,
      }}
    >
      {media.map((item, index) => (
        <Box
          key={`${item.id}-${index}`}
          sx={{
            position: 'relative',
            width: '100%',
            pt: aspectPadding,
            borderRadius,
            overflow: 'hidden',
            cursor: onPhotoClick ? 'pointer' : 'default',
            boxShadow: cardShadow,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': onPhotoClick
              ? {
                  transform: 'scale(1.02)',
                  boxShadow: 6,
                }
              : undefined,
            backgroundColor: theme.palette.grey[100],
          }}
          onClick={() => {
            if (onPhotoClick) {
              onPhotoClick(index);
            }
          }}
        >
          <Box
            component="img"
            src={item.image_url}
            alt={item.caption || `Photo ${index + 1}`}
            loading="lazy"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

export default GalleryGrid;

